import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import AppLayout from './components/layout/AppLayout.jsx'
import AuthGate from './components/AuthGate.jsx'
import OccupantLayout from './components/layout/OccupantLayout.jsx'

// Route-level code splitting. Only the shell (AppLayout / OccupantLayout /
// AuthGate) ships in the initial bundle; every page is fetched on demand.
const OverviewPage = lazy(() => import('./pages/OverviewPage.jsx'))
const PropertiesPage = lazy(() => import('./pages/PropertiesPage.jsx'))
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage.jsx'))
const IncomeStatementPage = lazy(() => import('./pages/IncomeStatementPage.jsx'))
const OwnerStatementsPage = lazy(() => import('./pages/OwnerStatementsPage.jsx'))
const InquiriesPage = lazy(() => import('./pages/InquiriesPage.jsx'))
const SiteAnalyticsPage = lazy(() => import('./pages/SiteAnalyticsPage.jsx'))
const CapRatesPage = lazy(() => import('./pages/CapRatesPage.jsx'))
const OwnerDrawsPage = lazy(() => import('./pages/OwnerDrawsPage.jsx'))
const BankAccountsPage = lazy(() => import('./pages/BankAccountsPage.jsx'))
const RentRollPage = lazy(() => import('./pages/RentRollPage.jsx'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.jsx'))
const CashFlowPage = lazy(() => import('./pages/CashFlowPage.jsx'))
const TopCostsPage = lazy(() => import('./pages/TopCostsPage.jsx'))
const TaxPage = lazy(() => import('./pages/TaxPage.jsx'))
const MonthlyPnlPage = lazy(() => import('./pages/MonthlyPnlPage.jsx'))
const BillsPage = lazy(() => import('./pages/BillsPage.jsx'))
const ReportsPage = lazy(() => import('./pages/ReportsPage.jsx'))
const SettingsPage = lazy(() => import('./pages/SettingsPage.jsx'))
const LoginPage = lazy(() => import('./pages/LoginPage.jsx'))
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage.jsx'))
const UsersPage = lazy(() => import('./pages/UsersPage.jsx'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage.jsx'))
const RenewalsPage = lazy(() => import('./pages/RenewalsPage.jsx'))

// Occupant (renter) portal pages.
const PortalHomePage = lazy(() => import('./pages/tenant/PortalHomePage.jsx'))
const MyLeasePage = lazy(() => import('./pages/tenant/MyLeasePage.jsx'))
const MyPaymentsPage = lazy(() => import('./pages/tenant/MyPaymentsPage.jsx'))
const MyMaintenancePage = lazy(() => import('./pages/tenant/MyMaintenancePage.jsx'))
const MyDocumentsPage = lazy(() => import('./pages/tenant/MyDocumentsPage.jsx'))

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700"
        aria-label="Loading"
      />
    </div>
  )
}

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/change-password"
          element={
            <AuthGate skipPasswordCheck>
              <ChangePasswordPage />
            </AuthGate>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <AuthGate>
              <AppLayout />
            </AuthGate>
          }
        >
          <Route index element={<OverviewPage />} />
          <Route path="inquiries" element={<InquiriesPage />} />
          <Route path="site-analytics" element={<SiteAnalyticsPage />} />
          <Route path="cap-rates" element={<CapRatesPage />} />
          <Route path="owner-draws" element={<OwnerDrawsPage />} />
          <Route path="bank-accounts" element={<BankAccountsPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:code" element={<PropertyDetailPage />} />
          <Route path="income-statement" element={<IncomeStatementPage />} />
          <Route path="statements" element={<OwnerStatementsPage />} />
          <Route path="rent-roll" element={<RentRollPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="cash-flow" element={<CashFlowPage />} />
          <Route path="top-costs" element={<TopCostsPage />} />
          <Route path="tax" element={<TaxPage />} />
          <Route path="monthly-pnl" element={<MonthlyPnlPage />} />
          <Route path="bills" element={<BillsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          {/* MaintenancePage renders differently based on role but is
              reachable by both admin and viewer — no admin gate here. The
              router (/api/maintenance) row-filters by role internally. */}
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route
            path="renewals"
            element={
              <AuthGate requireRole="admin">
                <RenewalsPage />
              </AuthGate>
            }
          />
          <Route
            path="users"
            element={
              <AuthGate requireRole="admin">
                <UsersPage />
              </AuthGate>
            }
          />
        </Route>

        {/* Occupant portal — role='occupant' only. AuthGate bounces staff
            back to /dashboard and unauthenticated visitors to /login.
            Every /api/renter/* endpoint the pages call is likewise gated
            server-side by requireOccupant. */}
        <Route
          path="/portal"
          element={
            <AuthGate requireOccupant>
              <OccupantLayout />
            </AuthGate>
          }
        >
          <Route index element={<PortalHomePage />} />
          <Route path="lease" element={<MyLeasePage />} />
          <Route path="payments" element={<MyPaymentsPage />} />
          <Route path="maintenance" element={<MyMaintenancePage />} />
          <Route path="maintenance/:id" element={<MyMaintenancePage />} />
          <Route path="documents" element={<MyDocumentsPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
